import { Panel } from "../../../components/panels/Panel";
import { DiorDigitalTwin } from "./DiorDigitalTwin";
import { InputText } from "../../../components/forms/InputText";
import { ArrowLeftCircleIcon, CheckIcon, InboxIcon } from "@heroicons/react/20/solid";
import { Select } from "../../../components/forms/Select";
import { patchData, postData, useGetData } from "../../../services/api/api-tools";
import { Loader } from "../../../components/loading/Loader";
import { useEffect, useState } from "react";
import { DiorSortableStepsList } from "./DiorSortableStepsList";
import { useAlert } from "../../../contexts/AlertContext";
import { useSocket } from "../../../contexts/SocketContext";

export const DiorLiveSetupPage = () => {

  const { sendEvent } = useSocket();
  const { showAlert } = useAlert();

  const newPatternPlaceholder = {
    id: -1,
    name: '',
    pattern_data: [{ name: 'scheme', display_text: 'scheme', value: 'json', json: [] }],
  };
  const [selectedPattern, setSelectedPattern] = useState(newPatternPlaceholder);
  const [steps, setSteps] = useState([]);
  const [newPatternName, setNewPatternName] = useState('');

  useEffect(() => {
    if (!selectedPattern?.pattern_data || !selectedPattern?.pattern_data[0]?.json?.length)
      setSteps([]);
    else
      setSteps(selectedPattern.pattern_data[0].json);
  }, [selectedPattern]);

  const { data: patterns, isLoading: patternsAreLoading, refetch: refetchPatterns } = useGetData('patterns', `patterns`);

  const handleSelectPattern = (pattern) => {
    setSelectedPattern(pattern);
    setNewPatternName(pattern.name);
  };

  const handlePoolClick = (pool, poolSchemeIndex) => {
    setSteps((steps) => [...steps, {
        id: crypto.randomUUID(), // useful to generate sortable list elements keys and prevent re-render
        pool_id: poolSchemeIndex,
        duration_min: 0, duration_max: 60
      }]
    );
  };

  const onChangeDuration = (stepId, option, value) => {
    setSteps(steps =>
      steps.map((item, i) => {
        if (item.id !== stepId) return item;

        const numericValue = Number(value) || 0;

        if (option === 'min') return { ...item, duration_min: numericValue };
        else if (option === 'max') return { ...item, duration_max: numericValue };

        return item;
      })
    );
  };

  const onDeleteStep = (stepId) => {
    setSteps(prev => prev.filter(step => step.id !== stepId));
  };

  const isValidProcessCode = (value) => /^[A-Za-z]{2}\d{3}$/.test(value);

  const checkValidDurations = () => !steps.some((step) => !(step.duration_min < step.duration_max));

  const savePattern = async () => {
    if (steps.length === 0){
      showAlert('error', 'Please select at least one step.');
      return false;
    }

    if (selectedPattern.id === -1 && newPatternName === '') {
      showAlert('error', 'Please enter a name for the process.');
      return false;
    }

    if (!isValidProcessCode(newPatternName)) {
      showAlert('error', 'Please use the format "LLNNN" (e.g. GA170).');
      return false;
    }

    if (!checkValidDurations()){
      showAlert('error', 'Minimum durations must be lower than maximum duration.');
      return false;
    }

    try {
      // Creation of a new pattern
      if (selectedPattern.id === -1) {
        const body = {
          name: newPatternName.toUpperCase(),
          is_active: false,
          last_modified: new Date().toISOString(),
          commentary: '',
          pattern_event_types: [],
          workstation_id: 1,
          pattern_datas: [{ name: 'scheme', display_text: 'scheme', value: 'json', json: steps}],
        };

        const response = await postData('patterns', body);
      }
      // Update of an existing pattern
      else {
        const response = await patchData(
          `patterns/${selectedPattern.id}/info`,
          { pattern_datas: [{ name: 'scheme', display_text: 'scheme', value: 'json', json: steps}] }
        );
      }

      const { data: newPatterns } = await refetchPatterns();

      const patternToSelect = newPatterns.find((pattern) => pattern.name.toLowerCase() === newPatternName.toLowerCase());
      if (patternToSelect) setSelectedPattern(patternToSelect);

      // Send data to the engine
      sendEvent('current_data', { 'process_names': newPatterns.map((pattern) => pattern.name) });

      showAlert('success', 'The process has been updated.');
    } catch (err) {
      showAlert('error', 'Oops! An error occurred.');
      console.error('Something bad happened:', err);
    }
  };

  if (patternsAreLoading) return <Loader/>;

  return (
    <Panel title="PROCESS SETUP" category="main">
      <div className="h-full flex gap-4">
        <div className="h-full min-w-[600px] max-w-[600px]">
          <Panel title="PROCESS DETAILS" className="rounded-md">
            {/* Process selection */}
            <Select
              items={patterns}
              value={selectedPattern}
              setValue={handleSelectPattern}
              itemToString={(item) => item?.name}
              placeholder="Create a new process"
              placeholderValue={newPatternPlaceholder}
              theme="dark"
              size="lg"
            />

            {
              selectedPattern.id === -1 &&
                <InputText theme="dark" size="lg" placeholder="Name of the process"
                           value={newPatternName} setValue={setNewPatternName}
                           helper='Please use the format "LLNNN" (e.g. GA170)' className="w-full mt-4"/>
            }

            {/* Process steps */}
            <div className="h-full bg-perception-black-500 rounded-md mt-4 p-2 flex flex-col overflow-y-auto overflow-x-hidden">
              <DiorSortableStepsList
                steps={steps}
                onReorder={setSteps}
                onChangeDuration={onChangeDuration}
                onDeleteStep={onDeleteStep}
              />
              {
                steps.length === 0 &&
                  <div className='flex flex-col items-center justify-center h-full text-perception-gray-600 text-xl text-center'>
                    <InboxIcon className='size-16' />
                    <span className='mt-2'>No step defined yet</span>
                    <span className="text-sm">Click on a pool to add it to the selected process.</span>
                  </div>
              }
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex justify-between">
              <button className="text-white px-4 py-2 flex items-center bg-perception-gray-700 rounded-md" onClick={() => window.location = '/interface/live/1'}>
                <ArrowLeftCircleIcon aria-hidden="true" className="size-5 mr-2" /> Back
              </button>
              <button className="text-white px-4 py-2 flex items-center bg-perception-blue rounded-md" onClick={savePattern}>
                <CheckIcon aria-hidden="true" className="size-5 mr-2" /> Save
              </button>
            </div>
          </Panel>
        </div>
        <div className="h-full min-w-0 flex-1">
          <DiorDigitalTwin cartsList={[]} onPoolClick={handlePoolClick} />
        </div>
      </div>
    </Panel>
  );
};